import { usePricingContext } from '@vp/react-pricing-context';
import { Box, ListPrice, Pricing, SkeletonText, SkeletonTextLine } from '@vp/swan';
import { VATMessage, priceFromFloat } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import type { Currency } from '@99designs/design-services-common';
import type { Price } from '../../types';

interface PriceProps {
    price?: Price;
    loading?: boolean;
    showFromPrice?: boolean;
}

export function Price({ price, loading, showFromPrice }: PriceProps) {
    if (!price && !loading) {
        return null;
    }

    return (
        <Box data-testid="price-container" aria-live="polite" aria-busy={loading}>
            <PriceContent loading={loading} price={price} showFromPrice={showFromPrice} />
        </Box>
    );
}

const PriceContent = ({ loading, price, showFromPrice }: PriceProps) => {
    const pricingContext = usePricingContext();

    if (loading) {
        return (
            <SkeletonText data-testid="skeleton-text">
                <SkeletonTextLine width="narrow"></SkeletonTextLine>
            </SkeletonText>
        );
    }

    if (!price) {
        return null;
    }

    const amount = pricingContext?.vatInclusive ? price.listPrice.taxed : price.listPrice.untaxed;

    return showFromPrice ? (
        <Pricing>
            <ListPrice>
                {__('From {{price}}', {
                    price: priceFromFloat({ amount, currency: price.currency as Currency }),
                    _context:
                        'Starting price for a design service product displayed alongside the product name.',
                })}
            </ListPrice>
            <VATMessage />
        </Pricing>
    ) : (
        <Pricing>
            <ListPrice>
                {priceFromFloat({ amount, currency: price.currency as Currency })}
            </ListPrice>
            <VATMessage />
        </Pricing>
    );
};
